import { useState, useEffect } from 'react';
import { useConfirmation } from '../../components/ConfirmationModal';

const AppearanceSettings = ({ settings, onSave, onChange }) => {
  const { showConfirmation, ConfirmationComponent } = useConfirmation();
  const [formData, setFormData] = useState({
    theme: settings?.theme || 'light',
    primaryColor: settings?.primaryColor || '#007bff',
    secondaryColor: settings?.secondaryColor || '#6c757d',
    successColor: settings?.successColor || '#28a745',
    warningColor: settings?.warningColor || '#ffc107',
    dangerColor: settings?.dangerColor || '#dc3545',
    fontFamily: settings?.fontFamily || 'system',
    fontSize: settings?.fontSize || 'medium',
    compactMode: settings?.compactMode || false,
    showBrandingOnDocuments: settings?.showBrandingOnDocuments ?? true,
    customCSS: settings?.customCSS || '',
    logoPosition: settings?.logoPosition || 'top-left',
    logoSize: settings?.logoSize || 'medium',
    documentTemplate: settings?.documentTemplate || 'modern',
    brandColors: settings?.brandColors || {
      primary: '#007bff',
      secondary: '#6c757d'
    }
  });

  const [previewMode, setPreviewMode] = useState('interface');

  useEffect(() => {
    if (settings) {
      setFormData({
        theme: settings.theme || 'light',
        primaryColor: settings.primaryColor || '#007bff',
        secondaryColor: settings.secondaryColor || '#6c757d',
        successColor: settings.successColor || '#28a745',
        warningColor: settings.warningColor || '#ffc107',
        dangerColor: settings.dangerColor || '#dc3545',
        fontFamily: settings.fontFamily || 'system',
        fontSize: settings.fontSize || 'medium',
        compactMode: settings.compactMode || false,
        showBrandingOnDocuments: settings.showBrandingOnDocuments ?? true,
        customCSS: settings.customCSS || '',
        logoPosition: settings.logoPosition || 'top-left',
        logoSize: settings.logoSize || 'medium',
        documentTemplate: settings.documentTemplate || 'modern',
        brandColors: settings.brandColors || {
          primary: '#007bff',
          secondary: '#6c757d'
        }
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    if (onChange) onChange();
  };

  const handleColorChange = (colorName, value) => {
    setFormData(prev => ({
      ...prev,
      [colorName]: value,
      brandColors: {
        ...prev.brandColors,
        [colorName]: value
      }
    }));
    
    if (onChange) onChange();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const resetToDefaults = async () => {
    const confirmed = await showConfirmation({
      title: 'Reset Appearance',
      message: 'Reset all appearance settings to defaults?',
      confirmText: 'Reset Settings',
      variant: 'warning'
    });
    
    if (confirmed) {
      const defaults = {
        theme: 'light',
        primaryColor: '#007bff',
        secondaryColor: '#6c757d',
        successColor: '#28a745',
        warningColor: '#ffc107',
        dangerColor: '#dc3545',
        fontFamily: 'system',
        fontSize: 'medium',
        compactMode: false,
        showBrandingOnDocuments: true,
        customCSS: '',
        logoPosition: 'top-left',
        logoSize: 'medium',
        documentTemplate: 'modern',
        brandColors: {
          primary: '#007bff',
          secondary: '#6c757d'
        }
      };
      setFormData(defaults);
      if (onChange) onChange();
    }
  };

  const previewTheme = (theme) => {
    // In a real app, this would apply the theme temporarily
    alert(`Theme preview functionality will be implemented. Selected: ${theme}`);
  };

  const colorPresets = [
    { name: 'Default Blue', primary: '#007bff', secondary: '#6c757d' },
    { name: 'Green', primary: '#28a745', secondary: '#20c997' },
    { name: 'Purple', primary: '#6f42c1', secondary: '#e83e8c' },
    { name: 'Orange', primary: '#fd7e14', secondary: '#ffc107' },
    { name: 'Red', primary: '#dc3545', secondary: '#fd7e14' },
    { name: 'Dark', primary: '#343a40', secondary: '#6c757d' }
  ];

  return (
    <>
      <ConfirmationComponent />
      <div className="card">
      <div className="card-header">
        <h6 className="mb-0">
          <i className="fas fa-palette me-2"></i>
          Appearance Settings
        </h6>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Theme Selection */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Theme</h6>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Theme Mode</label>
              <div className="row">
                <div className="col-4">
                  <div className="card text-center" style={{ cursor: 'pointer' }} onClick={() => handleChange({target: {name: 'theme', value: 'light'}})}>
                    <div className={`card-body ${formData.theme === 'light' ? 'border-primary bg-light' : ''}`}>
                      <i className="fas fa-sun fa-2x text-warning mb-2"></i>
                      <div>Light</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center" style={{ cursor: 'pointer' }} onClick={() => handleChange({target: {name: 'theme', value: 'dark'}})}>
                    <div className={`card-body bg-dark text-light ${formData.theme === 'dark' ? 'border-primary' : ''}`}>
                      <i className="fas fa-moon fa-2x text-info mb-2"></i>
                      <div>Dark</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center" style={{ cursor: 'pointer' }} onClick={() => handleChange({target: {name: 'theme', value: 'auto'}})}>
                    <div className={`card-body ${formData.theme === 'auto' ? 'border-primary bg-light' : ''}`}>
                      <i className="fas fa-adjust fa-2x text-secondary mb-2"></i>
                      <div>Auto</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Interface Options</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="compactMode"
                  checked={formData.compactMode}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Compact mode (reduced spacing)
                </label>
              </div>
            </div>
          </div>

          {/* Color Scheme */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Color Scheme</h6>
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Color Presets</label>
              <div className="row">
                {colorPresets.map((preset, index) => (
                  <div key={index} className="col-md-2 mb-2">
                    <div 
                      className="card text-center" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        handleColorChange('primaryColor', preset.primary);
                        handleColorChange('secondaryColor', preset.secondary);
                      }}
                    >
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-center mb-1">
                          <div 
                            className="rounded-circle me-1" 
                            style={{ width: '20px', height: '20px', backgroundColor: preset.primary }}
                          ></div>
                          <div 
                            className="rounded-circle" 
                            style={{ width: '20px', height: '20px', backgroundColor: preset.secondary }}
                          ></div>
                        </div>
                        <small>{preset.name}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="col-md-3 mb-3">
              <label className="form-label">Primary Color</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="primaryColor"
                  value={formData.primaryColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  className="form-control"
                  value={formData.primaryColor}
                  onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                />
              </div>
            </div>
            
            <div className="col-md-3 mb-3">
              <label className="form-label">Secondary Color</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="secondaryColor"
                  value={formData.secondaryColor}
                  onChange={handleChange}
                />
                <input
                  type="text"
                  className="form-control"
                  value={formData.secondaryColor}
                  onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                />
              </div>
            </div>
            
            <div className="col-md-2 mb-3">
              <label className="form-label">Success</label>
              <input
                type="color"
                className="form-control form-control-color"
                name="successColor"
                value={formData.successColor}
                onChange={handleChange}
              />
            </div>
            
            <div className="col-md-2 mb-3">
              <label className="form-label">Warning</label>
              <input
                type="color"
                className="form-control form-control-color"
                name="warningColor"
                value={formData.warningColor}
                onChange={handleChange}
              />
            </div>
            
            <div className="col-md-2 mb-3">
              <label className="form-label">Danger</label>
              <input
                type="color"
                className="form-control form-control-color"
                name="dangerColor"
                value={formData.dangerColor}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Typography */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Typography</h6>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Font Family</label>
              <select
                className="form-select"
                name="fontFamily"
                value={formData.fontFamily}
                onChange={handleChange}
              >
                <option value="system">System Default</option>
                <option value="arial">Arial</option>
                <option value="helvetica">Helvetica</option>
                <option value="times">Times New Roman</option>
                <option value="georgia">Georgia</option>
                <option value="verdana">Verdana</option>
                <option value="roboto">Roboto</option>
                <option value="open-sans">Open Sans</option>
                <option value="lato">Lato</option>
              </select>
            </div>
            
            <div className="col-md-6 mb-3">
              <label className="form-label">Font Size</label>
              <select
                className="form-select"
                name="fontSize"
                value={formData.fontSize}
                onChange={handleChange}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
                <option value="extra-large">Extra Large</option>
              </select>
            </div>
          </div>

          {/* Document Branding */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Document Branding</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="showBrandingOnDocuments"
                  checked={formData.showBrandingOnDocuments}
                  onChange={handleChange}
                />
                <label className="form-check-label">
                  Show company branding on invoices and quotes
                </label>
              </div>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Logo Position</label>
              <select
                className="form-select"
                name="logoPosition"
                value={formData.logoPosition}
                onChange={handleChange}
              >
                <option value="top-left">Top Left</option>
                <option value="top-center">Top Center</option>
                <option value="top-right">Top Right</option>
                <option value="center">Center</option>
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Logo Size</label>
              <select
                className="form-select"
                name="logoSize"
                value={formData.logoSize}
                onChange={handleChange}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            
            <div className="col-md-4 mb-3">
              <label className="form-label">Document Template</label>
              <select
                className="form-select"
                name="documentTemplate"
                value={formData.documentTemplate}
                onChange={handleChange}
              >
                <option value="classic">Classic</option>
                <option value="modern">Modern</option>
                <option value="minimal">Minimal</option>
                <option value="professional">Professional</option>
              </select>
            </div>
          </div>

          {/* Custom CSS */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Advanced Customization</h6>
            </div>
            
            <div className="col-12 mb-3">
              <label className="form-label">Custom CSS</label>
              <textarea
                className="form-control"
                name="customCSS"
                value={formData.customCSS}
                onChange={handleChange}
                rows="8"
                placeholder="/* Add your custom CSS here */&#10;.custom-class {&#10;  color: #333;&#10;  font-weight: bold;&#10;}"
                style={{ fontFamily: 'monospace' }}
              />
              <div className="form-text">
                Add custom CSS to override default styles. Use with caution.
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="border-bottom pb-2 mb-3">Preview</h6>
            </div>
            
            <div className="col-12 mb-3">
              <div className="btn-group mb-3">
                <button
                  type="button"
                  className={`btn ${previewMode === 'interface' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setPreviewMode('interface')}
                >
                  Interface
                </button>
                <button
                  type="button"
                  className={`btn ${previewMode === 'document' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setPreviewMode('document')}
                >
                  Document
                </button>
              </div>
              
              <div className="border rounded p-3" style={{ minHeight: '200px' }}>
                {previewMode === 'interface' ? (
                  <div>
                    <div className="d-flex align-items-center mb-3" style={{ color: formData.primaryColor }}>
                      <h5 className="mb-0">Sample Interface</h5>
                    </div>
                    <div className="btn-group mb-3">
                      <button className="btn btn-sm" style={{ backgroundColor: formData.primaryColor, color: 'white' }}>
                        Primary Button
                      </button>
                      <button className="btn btn-outline-secondary btn-sm">Secondary</button>
                    </div>
                    <div className="card">
                      <div className="card-header" style={{ backgroundColor: formData.primaryColor + '10' }}>
                        Sample Card Header
                      </div>
                      <div className="card-body">
                        <p>This is how your interface will look with the selected colors and font settings.</p>
                        <div className="row">
                          <div className="col-3">
                            <span className="badge" style={{ backgroundColor: formData.successColor }}>Success</span>
                          </div>
                          <div className="col-3">
                            <span className="badge" style={{ backgroundColor: formData.warningColor }}>Warning</span>
                          </div>
                          <div className="col-3">
                            <span className="badge" style={{ backgroundColor: formData.dangerColor }}>Danger</span>
                          </div>
                          <div className="col-3">
                            <span className="badge" style={{ backgroundColor: formData.secondaryColor }}>Secondary</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-3">
                      <h5 style={{ color: formData.primaryColor }}>INVOICE #INV-001</h5>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <strong>From:</strong><br />
                        Your Company Name<br />
                        123 Business Street<br />
                        City, State 12345
                      </div>
                      <div className="col-6 text-end">
                        <strong>To:</strong><br />
                        Client Name<br />
                        456 Client Avenue<br />
                        City, State 67890
                      </div>
                    </div>
                    <hr />
                    <table className="table table-sm">
                      <thead style={{ backgroundColor: formData.primaryColor + '20' }}>
                        <tr>
                          <th>Item</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Rate</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Sample Service</td>
                          <td className="text-end">1</td>
                          <td className="text-end">£100.00</td>
                          <td className="text-end">£100.00</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="text-end">
                      <strong style={{ color: formData.primaryColor }}>Total: £100.00</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary">
              <i className="fas fa-save me-1"></i>
              Save Appearance Settings
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={resetToDefaults}>
              <i className="fas fa-undo me-1"></i>
              Reset to Defaults
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default AppearanceSettings;